import { faCloudUpload, faLessThanEqual } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import "./websiteBuilderFileDropZone.scss";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { useTranslation } from "react-i18next";

export type TWebsiteBuilderFileDropZoneTheme = "light" | "dark";

export type TWebsiteBuilderFileType = "ico" | "svg" | "png" | "jpg" | "mp4" | "mov" | "webm";

interface IWebsiteBuilderImageDimension {
  type: "dimension";
  width: number;
  height: number;
}

interface IWebsiteBuilderImageDimensionRange {
  type: "range-dimension";
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
}

export type TWebsiteBuilderImageDimensionValidation = IWebsiteBuilderImageDimension | IWebsiteBuilderImageDimensionRange;

export interface IWebsiteBuilderFilePreview {
  file: File;
  url: string;
}

const WEBSITE_BUILDER_FILE_MEGABYTE = 1_048_576;

interface IWebsiteBuilderFileDropZoneProps {
  theme: TWebsiteBuilderFileDropZoneTheme;
  condensed?: boolean;
  allowedFileTypes: TWebsiteBuilderFileType[];
  maxImageMegabytes?: number;
  maxVideoMegabytes?: number;
  imageDimensionValidations?: TWebsiteBuilderImageDimensionValidation[];
  filePreview?: IWebsiteBuilderFilePreview;
  setFilePreview?: (updatedFilePreview: IWebsiteBuilderFilePreview) => void;
  saveFile?: (file: File) => void;
}

export function WebsiteBuilderFileDropZone(props: IWebsiteBuilderFileDropZoneProps) {
  const {
    theme,
    condensed,
    allowedFileTypes,
    maxImageMegabytes,
    maxVideoMegabytes,
    imageDimensionValidations,
    filePreview,
    setFilePreview,
    saveFile,
  } = props;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [draggingOverZone, setDraggingOverZone] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      if (filePreview?.url) {
        URL.revokeObjectURL(filePreview.url);
      }
    };
  }, [filePreview?.url]);

  function handleClickZone() {
    fileInputRef.current?.click();
  }

  function handleDragOverZone(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    setDraggingOverZone(true);
  }

  function handleDragLeaveZone(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    setDraggingOverZone(false);
  }

  function handleValidatedFile(file: File, existingUrl?: string) {
    if (setFilePreview) {
      const updatedFilePreview: IWebsiteBuilderFilePreview = {
        file,
        url: existingUrl ?? URL.createObjectURL(file),
      };
  
      setFilePreview(updatedFilePreview);
    } else if (saveFile) {
      saveFile(file);
    }
  }

  function isImageFileType(type: TWebsiteBuilderFileType) {
    return type === "ico" || type === "svg" || type === "png" || type === "jpg";
  }

  function isVideoFileType(type: TWebsiteBuilderFileType) {
    return type === "mp4" || type === "mov" || type === "webm";
  }

  function getMaxMegabytes(fileType: TWebsiteBuilderFileType) {
    if (isImageFileType(fileType) && maxImageMegabytes != null) {
      return maxImageMegabytes;
    } else if (isVideoFileType(fileType) && maxVideoMegabytes != null) {
      return maxVideoMegabytes;
    } else {
      return null;
    }
  }

  function handleFileChange(file: File) {
    if (!file) {
      dispatch(showError(t("File doesn't exist")));
      return;
    }

    let fileType: TWebsiteBuilderFileType = null;

    if (file.type === "image/x-icon" || file.type === "image/vnd.microsoft.icon") {
      fileType = "ico";
    } else if (file.type === "image/svg+xml") {
      fileType = "svg";
    } else if (file.type === "image/png") {
      fileType = "png";
    } else if (file.type === "image/jpeg") {
      fileType = "jpg";
    } else if (file.type === "video/mp4") {
      fileType = "mp4";
    } else if (file.type === "video/quicktime") {
      fileType = "mov";
    } else if (file.type === "video/webm") {
      fileType = "webm";
    }

    if (!fileType || !allowedFileTypes.includes(fileType)) {
      dispatch(showError(t("Invalid file type")));
      return;
    }

    const maxMegabytes = getMaxMegabytes(fileType);

    if (maxMegabytes != null && file.size > maxMegabytes * WEBSITE_BUILDER_FILE_MEGABYTE) {
      dispatch(showError(t("File size is too large")));
      return;
    }

    if (file.type.startsWith("image/") && imageDimensionValidations?.length > 0) {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        const validImage = imageDimensionValidations.some(validation => {
          if (validation.type === "dimension") {
            return width === validation.width && height === validation.height;
          } else if (validation.type === "range-dimension") {
            const { minWidth, maxWidth, minHeight, maxHeight } = validation;

            return (
              width >= minWidth &&
              width <= maxWidth &&
              height >= minHeight &&
              height <= maxHeight
            );
          } else {
            return false;
          }
        });

        if (!validImage) {
          dispatch(showError(`${t("Invalid image dimensions")} (${width}x${height})`));
          URL.revokeObjectURL(url);
        } else {
          handleValidatedFile(file, url);
        }
      };

      img.onerror = () => {
        dispatch(showError(t("Error loading image")));
        URL.revokeObjectURL(url);
      };

      img.src = url;
    } else {
      handleValidatedFile(file);
    }
  }

  function handleDropFileInZone(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    
    setDraggingOverZone(false);

    handleFileChange(e.dataTransfer?.files?.[0]);
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleFileChange(e.target?.files?.[0]);
  }

  function getReadableFileType(type: TWebsiteBuilderFileType) {
    switch (type) {
      case "ico": return "ICO";
      case "svg": return "SVG";
      case "png": return "PNG";
      case "jpg": return "JPG";
      case "mp4": return "MP4";
      case "mov": return "MOV";
      case "webm": return "WEBM";
      default: return "";
    }
  }

  function renderFileTypes(types: TWebsiteBuilderFileType[]) {
    return types.map((type, index) =>
      <span key={index}>{`${getReadableFileType(type)}${index < types.length - 1 ? ", " : " "}`}</span>,
    );
  }

  function renderMaxMegabytes(megabytes: number) {
    return (
      <>
        <FontAwesomeIcon
          className="zone-secondary-message-less-than-equal"
          icon={faLessThanEqual}
        />
        {megabytes}MB
      </>
    );
  }

  function renderFileRequirementGroup(title: string, types: TWebsiteBuilderFileType[], showTitle: boolean, maxMegabytes?: number) {
    return (
      <p className="zone-secondary-message">
        {showTitle && `${title}: `}{renderFileTypes(types)}
        {maxMegabytes != null && renderMaxMegabytes(maxMegabytes)}
      </p>
    );
  }

  function renderFileRequirements() {
    const imageFileTypes = allowedFileTypes.filter(type => isImageFileType(type));
    const videoFileTypes = allowedFileTypes.filter(type => isVideoFileType(type));

    const fileRequirementGroups = [
      {
        title: t("Images"),
        types: imageFileTypes,
        maxMegabytes: maxImageMegabytes,
      },
      {
        title: t("Videos"),
        types: videoFileTypes,
        maxMegabytes: maxVideoMegabytes,
      },
    ].filter(group => group.types.length > 0);

    const showTitle = fileRequirementGroups.length > 1;

    return (
      <>
        {fileRequirementGroups.map(group =>
          <React.Fragment key={group.title}>
            {renderFileRequirementGroup(group.title, group.types, showTitle, group.maxMegabytes)}
          </React.Fragment>,
        )}
      </>
    );
  }
  
  return (
    <div
      className={classNames("website-builder-file-drop-zone", {
        "file-drop-zone-dragging-over": draggingOverZone,
        "file-drop-zone-light": theme === "light",
        "file-drop-zone-dark": theme === "dark",
        "file-drop-zone-condensed": condensed,
      })}
      onClick={handleClickZone}
      onDragOver={handleDragOverZone}
      onDragLeave={handleDragLeaveZone}
      onDrop={handleDropFileInZone}
    >
      <div className="zone-cloud-icon-outer-circle">
        <div className="zone-cloud-icon-inner-circle">
          <FontAwesomeIcon className="zone-cloud-icon" size={condensed ? "1x" : "lg"} icon={faCloudUpload} />
        </div>
      </div>
      <div>
        <p className="zone-primary-message">{t("Click to upload or drag and drop")}</p>
        {renderFileRequirements()}
        {imageDimensionValidations?.length > 0 && (
          <p className="zone-secondary-message">
            {imageDimensionValidations.map((validation, index) => 
              <span key={index}>
                {validation.type === "dimension" ? (
                  <span>{validation.width}x{validation.height}px</span>
                ) : (
                  <span>{validation.minWidth}-{validation.maxWidth}x{validation.minHeight}-{validation.maxHeight}px</span>
                )}
                {index < imageDimensionValidations.length - 1 ? ", " : ""}
              </span>,
            )}
          </p>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileInputChange}
      />
    </div>
  );
}